import PropTypes from "prop-types";
import { useContext, useState } from "react";
import { Link } from "react-router-dom";
import { authenticateWpUser, createWpUser } from "../../../api/wordpress";
import { sendOtp, verifyOtp, resendOtp } from "../../../api/auth";
import { UserContext } from "../../../contexts/user.context";
import {
  createAuthUserWithEmailAndPassword,
  createUserDocumentFromAuth,
} from "../../../utils/firebase";
import InputField from "../../fields/input-field/form-input.component";
import PasswordInputField from "../../fields/input-field/password-input.component";
import SuccessModal from "../../modals/success-modal.component";
import TermsConditions from "../../modals/terms&conditions-modal.component";
import OtpModal from "../../modals/otp-modal.component";
import AccountCreationModal from "../../modals/account-creation-modal.component";

const defaultFormFields = {
  full_name: "",
  user_name: "",
  user_email: "",
  user_pass: "",
};

const defaultErrorObject = {
  code: "",
  status: false,
  message: "",
};

const ReviewModal = ({ state, onClose, onConfirm, data, loading }) => {
  if (!state) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 p-4">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-md overflow-hidden">
        <div className="p-6">
          <div className="flex items-center justify-center mb-4 text-yellow-500">
            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-12 h-12">
              <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126zM12 15.75h.007v.008H12v-.008z" />
            </svg>
          </div>
          
          <h3 className="text-xl font-bold text-center text-gray-800 mb-2">Double Check Your Details</h3>
          <p className="text-sm text-center text-gray-500 mb-6">
            To prevent verification issues later, please ensure your name <b>matches your Government ID.</b>
          </p>

          <div className="bg-gray-50 p-4 rounded-md space-y-3 mb-6 text-sm">
            <div className="flex justify-between border-b pb-2">
              <span className="text-gray-500">Legal Name:</span>
              <span className="font-semibold text-gray-800 text-right">{data.full_name}</span>
            </div>
            <div className="flex justify-between border-b pb-2">
              <span className="text-gray-500">Email:</span>
              <span className="font-semibold text-gray-800 text-right">{data.user_email}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-500">Username:</span>
              <span className="font-semibold text-gray-800 text-right">{data.user_name}</span>
            </div>
          </div>

          <div className="flex gap-3">
            <button
              onClick={onClose}
              disabled={loading}
              className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50 font-medium disabled:opacity-50"
            >
              Edit
            </button>
            <button
              onClick={onConfirm}
              disabled={loading}
              className="flex-1 px-4 py-2 bg-primary text-white rounded-md hover:bg-blue-900 font-medium flex justify-center items-center disabled:opacity-70"
            >
              {loading ? (
                 <svg className="animate-spin h-5 w-5 text-white" viewBox="0 0 24 24">
                   <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                   <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"></path>
                 </svg>
              ) : "Confirm & Create"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

const RegisterForm = ({ onCompleted }) => {
  const [formFields, setFormFields] = useState(defaultFormFields);
  const { full_name, user_name, user_email, user_pass } = formFields;

  const [loading, setLoading] = useState(false);
  const [successModalState, setSuccessModalState] = useState(false);
  const [termModalState, setTermModalState] = useState(false);
  const [reviewModalState, setReviewModalState] = useState(false);
  const [otpModalState, setOtpModalState] = useState(false);
  const [otpLoading, setOtpLoading] = useState(false);
  const [otpError, setOtpError] = useState("");
  const [accountCreatingState, setAccountCreatingState] = useState(false);
  
  const [errorObj, setLoginError] = useState(defaultErrorObject);
  const [termsChecked, setTermsChecked] = useState(false);
  const [nameError, setNameError] = useState("");

  const { persistUser } = useContext(UserContext);

  const handleChange = (event) => {
    const { name, value } = event.target;
    setFormFields({ ...formFields, [name]: value });

    if (name === "full_name") {
        const parts = value.trim().split(/\s+/);
        if (parts.length < 2 && value.length > 3) {
            setNameError("Please enter your First and Last name.");
        } else {
            setNameError("");
        }
    }
  };

  const handleInitiateSubmit = (event) => {
    event.preventDefault();
    setLoginError(defaultErrorObject);

    const parts = full_name.trim().split(/\s+/);
    if (parts.length < 2) {
        setLoginError({ 
            status: true, 
            message: "Please enter your full legal name (First and Last name) as it appears on your ID." 
        });
        return;
    }

    if (!termsChecked) {
        setLoginError({
          status: true,
          message:
            "Please agree to the Terms of Service, Privacy Policy, Identity Verification, and Cancellation & Refund Policy.",
        });
        return;
    }

    setReviewModalState(true);
  };

  const handleFinalizeRegistration = async () => {
    setLoading(true);
    try {
      await sendOtp(user_email);
      setReviewModalState(false);
      setOtpError("");
      setOtpModalState(true);
    } catch (error) {
      const { message } = error.response ? error.response.data : error;
      setReviewModalState(false);
      setLoginError({
        status: true,
        message: message || "Failed to send OTP. Please try again.",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleResendOtp = async () => {
    try {
      await resendOtp(user_email);
    } catch (error) {
      const { message } = error.response ? error.response.data : error;
      throw new Error(message || "Failed to resend OTP.");
    }
  };

  const handleVerifyOtp = async (otp) => {
    setOtpLoading(true);
    setOtpError("");
    try {
      await verifyOtp(user_email, otp);
      // OTP verified, close OTP modal and show account creation loader
      setOtpModalState(false);
      setOtpLoading(false);
      setAccountCreatingState(true);
      
      // Now create accounts in the background
      const parts = formFields.full_name.trim().split(/\s+/);
      const first_name = parts[0];
      const last_name = parts.slice(1).join(" ");

      await createWpUser({
        ...formFields,
        first_name,
        last_name,
      });

      const { user } = await createAuthUserWithEmailAndPassword(
        user_email,
        user_pass
      );

      await createUserDocumentFromAuth(user, {
        first_name,
        last_name,
        user_name,
      });

      const response = await authenticateWpUser({
        username: user_email,
        password: user_pass,
      });

      persistUser(response.data);
       await fetch(`${process.env.REACT_APP_BASE_URL || "https://ruumies-vfd-nodejs-engine-juft.vercel.app"}/api/auth/send-welcome`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email: user_email, firstName: first_name })
      }).catch(err => console.error("Welcome email trigger failed", err));
      
      setAccountCreatingState(false);
      onCompleted();
    } catch (error) {
      setAccountCreatingState(false);
      const { message } = error.response ? error.response.data : error;
      setOtpError(message || "Account creation failed. Please try again.");
      setOtpModalState(true);
    } finally {
      setOtpLoading(false);
    }
  };

  return (
    <>
      <form
        id="register_form"
        className="font-light"
        autoComplete="off"
        onSubmit={handleInitiateSubmit}
      >
        {errorObj.status && (
          <div className="flex p-4 mb-4 text-sm text-red-700 bg-red-100 rounded-lg" role="alert">
            <svg className="inline flex-shrink-0 mr-3 w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd"></path>
            </svg>
            <div dangerouslySetInnerHTML={{ __html: errorObj.message }} />
          </div>
        )}

        <div className="mb-2">
            <InputField
            label="Full Name"
            type="text"
            name="full_name"
            value={full_name}
            required
            onChange={handleChange}
            />
            {nameError ? (
                <p className="text-red-500 text-xs -mt-8">{nameError}</p>
            ) : (
                <p className="text-amber-500 text-xs -mt-8">
                 Must match your Government ID (First & Last Name)
                </p>
            )}
        </div>

        <InputField
          label="Email"
          type="email"
          name="user_email"
          value={user_email}
          required
          onChange={handleChange}
        />
        <InputField
          label="Username"
          type="text"
          name="user_name"
          value={user_name}
          required
          onChange={handleChange}
        />
        <PasswordInputField
          label="Password"
          name="user_pass"
          value={user_pass}
          required
          onChange={handleChange}
        />
        
        <div className="flex items-center mb-4">
          <input 
            id="legal-documents-consent"
            type="checkbox" 
            required 
            checked={termsChecked}
            onChange={(e)=>setTermsChecked(e.target.checked)}
          />
          <label htmlFor="legal-documents-consent" className="ml-2 text-sm text-gray-600 cursor-pointer">
            I have read, understood, and consent to the Terms of Service, Privacy Policy, Identity Verification data processing,
            and Cancellation & Refund Policy. See all documents {" "}
             <Link to="/legal-documents" className="text-primary underline">
              here
            </Link>.
          </label>
        </div>

        <div className="field_wrapper mb-5">
          <button
            type="submit"
            className="w-full rounded-md text-white p-3 bg-primary hover:bg-blue-900 font-semibold cursor-pointer"
          >
            Create Account
          </button>
        </div>

        <ReviewModal 
            state={reviewModalState} 
            data={formFields}
            loading={loading}
            onClose={() => setReviewModalState(false)}
            onConfirm={handleFinalizeRegistration}
        />

        <SuccessModal
          action={() => setSuccessModalState(false)}
          state={successModalState}
          title="You are successfully Registered !"
        >
          <Link to="/login">
            <button className="w-full rounded-md text-white p-3 bg-primary hover:bg-blue-900 font-semibold cursor-pointer">
              Go To Login
            </button>
          </Link>
        </SuccessModal>

        <TermsConditions
          action={() => setTermModalState(false)}
          accept={()=>{
            setTermsChecked(true)
            setTermModalState(false)
          }}
          state={termModalState}
        />

        <OtpModal
          state={otpModalState}
          onClose={() => setOtpModalState(false)}
          onVerify={handleVerifyOtp}
          onResend={handleResendOtp}
          email={user_email}
          loading={otpLoading}
          error={otpError}
        />

        <AccountCreationModal
          state={accountCreatingState}
          message="OTP verified! Creating your account..."
        />
      </form>
    </>
  );
};

RegisterForm.propTypes = {
  onCompleted: PropTypes.func.isRequired,
};

export default RegisterForm;